import React, { useEffect, useMemo, useState } from 'react';

import { MenuItem } from '@mui/material';
import { cloneDeep, orderBy } from 'lodash';
import { useHistory, useLocation, useParams } from 'react-router-dom';

import useEventWithDimensions from 'hooks/useEventWithDimensions';

import { useVersionStore } from 'store/version.store';

import { IVersion } from 'api/queries/getProjectVersionsList.query';

import { CustomizedDropdownMenu } from 'components/common/CustomizedDropdownMenu/CustomizedDropdownMenu';
import { CustomizedTooltip } from 'components/common/CustomizedTooltip/CustomizedTooltip';
import { TooltipPosition } from 'components/common/CustomizedTooltip/customizedTooltip.enums';
import { MuiIcon } from 'components/common/Icons/base/MuiIcon/MuiIcon';
import classes from 'components/common/TreeProgramFilter/VersionSelect/versionSelect.module.scss';
import { prepareVersionSelectTitle } from 'components/common/TreeProgramFilter/VersionSelect/versionSelect.utils';

import { Button } from 'components/DesignSystem/Button';

const SelectTitle = ({ value }) => {
    return (
        <div>
            <CustomizedTooltip
                tooltipPosition={TooltipPosition.Top}
                tooltipContent={value}
                triggerElement={
                    <Button
                        data-testid={'version-name'}
                        variant={'secondary'}
                        endIcon={<MuiIcon icon={'keyboard_arrow_down'} color={'inherit'} />}
                        startIcon={<MuiIcon icon={'description'} type={'outlined'} color={'inherit'} />}
                    >
                        Version: {value}
                    </Button>
                }
            />
        </div>
    );
};

export const NotMemoizedVersionSelect = ({ versionsList }: { versionsList: IVersion[] }) => {
    const history = useHistory();
    const location = useLocation();
    const { sendEventWithDimensions } = useEventWithDimensions();
    const urlSearchParams = new URLSearchParams(location.search);
    const [sortedList, setSortedList] = useState(versionsList);
    const { id } = useParams<{ id: string }>();
    const idListLength = id?.split(',').length;
    const { setVersion } = useVersionStore();

    useEffect(() => {
        if (id) {
            const versionsListCopy = cloneDeep(versionsList);
            const sortedSelectedVersions = orderBy(versionsListCopy.slice(0, idListLength), ['versionDate', 'asc']);
            const versionsArrayWithoutSelected = versionsListCopy.slice(idListLength, versionsListCopy.length);
            setSortedList([...sortedSelectedVersions, ...versionsArrayWithoutSelected]);
            setVersion(versionsList.find((i) => i.versionHashCode === Number(id)) || null);
        }
    }, [versionsList, id]);

    const handleSingleVersionSelect = (version) => {
        const newLocation = location.pathname.replace(id, version.versionHashCode);
        const payload = { pathname: newLocation, search: '' };
        const searchParams = urlSearchParams.toString();
        if (searchParams !== '') {
            payload.search = searchParams;
        }
        sendEventWithDimensions({
            category: 'Version',
            action: 'Selection',
            label: prepareVersionSelectTitle(version),
        });
        setVersion(version);
        history.push(payload);
    };

    const selected = useMemo(() => versionsList?.find((i) => i.versionHashCode === Number(id)), [versionsList, id]);

    const selectTitleValue = prepareVersionSelectTitle(selected);

    return (
        <div className={classes.versionSelect}>
            {versionsList?.length ? (
                <CustomizedDropdownMenu
                    menuStyles={{
                        minWidth: '350px',
                    }}
                    triggerElement={<SelectTitle value={selectTitleValue} />}
                >
                    {sortedList.map((item) => (
                        <MenuItem key={item.id} onClick={() => handleSingleVersionSelect(item)}>
                            <div>{prepareVersionSelectTitle(item)}</div>
                        </MenuItem>
                    ))}
                </CustomizedDropdownMenu>
            ) : (
                <SelectTitle value={'No versions.'} />
            )}
        </div>
    );
};

export const VersionSelect = React.memo(NotMemoizedVersionSelect);
